<template>
    <b-modal
        v-model="modal"
        modal-class="apprentice-default-modal"
        :id="'group-show-rating-modal'"
        :ref="'group-show-rating-modal'"
        :title="$t('group.manage_assessments')"
        :cancel-title="$t('common.cancel')"
        cancel-variant="outline-primary"
        :ok-title="$t('common.save')"
        @ok="onOk"
        size="lg">
        <div class="ratings-forms-management">
            <div
                class="errors"
                v-if="hasError">
                <b-alert
                    show
                    variant="danger">
                    {{ $t('group.visibility_not_set') }}
                </b-alert>
            </div>

            <design-2021-apprentice-data-table
                class="rating-forms-overview-table"
                :fields="formFields"
                :items="ratingsForms">
                <template #options>
                    <div class="add-new-rating-form">
                        <b-button
                            class="btn-add btn-new-rating-form"
                            v-b-modal.create-rating-form-modal
                            variant="primary">
                            {{ $t('group.create_form') }}
                        </b-button>
                    </div>
                </template>

                <template #cell(visibility)="data">
                    <div class="rating-visibility">
                        <b-button
                            class="btn-visibility-all"
                            v-b-tooltip:hover
                            :class="data.item.visibility !== 1 ? 'disabled' : 'active'"
                            variant="primary"
                            :title="$t('group.visible_for_everyone')"
                            :id="`${data.item.rating_id}-btn-all`"
                            @click="data.item.visibility = 1">
                            <i class="fad fa-fw fa-users icon"></i>
                        </b-button>

                        <b-button
                            class="btn-visibility-teachers"
                            v-b-tooltip:hover
                            :class="data.item.visibility !== 2 ? 'disabled' : 'active'"
                            variant="primary"
                            :title="$t('group.visible_for_teachers')"
                            :id="`${data.item.rating_id}-btn-teachers`"
                            @click="data.item.visibility = 2">
                            <i class="fad fa-fw fa-user icon"></i>
                        </b-button>
                    </div>
                </template>

                <template #cell(connected)="data">
                    <div class="rating-connected">
                        <b-form-checkbox
                            class="connected-rating-toggle"
                            v-model="data.item.connected"
                            name="connected-rating-toggle"
                            switch>
                        </b-form-checkbox>
                    </div>
                </template>

                <template #cell(actions)="data">
                    <div class="management-actions">
                        <b-button
                            class="btn-edit btn-edit-rating-form"
                            v-b-tooltip:hover
                            variant="primary-icon-only"
                            :title="$t('common.edit')"
                            @click="editRatingForm(data.item)">
                        </b-button>
                        <b-button
                            class="btn-copy btn-duplicate-rating-form"
                            v-b-tooltip:hover
                            variant="primary-icon-only"
                            :title="$t('common.copy')"
                            @click="duplicateForm(data.item.rating_id)">
                        </b-button>
                        <b-button
                            class="btn-remove btn-remove-rating-form"
                            v-b-tooltip:hover
                            variant="danger-icon-only"
                            :title="$t('common.delete')"
                            @click="removeRatingForm(data.item.rating_id)">
                        </b-button>
                    </div>
                </template>
            </design-2021-apprentice-data-table>
        </div>
    </b-modal>
</template>

<script>
export default {
    name: 'design-2021-group-show-rating-modal-component',
    props: {
        groupIn: Object,
    },
    data() {
        return {
            formFields: [
                {
                    key: 'title',
                    label: this.$t('common.form'),
                },
                {
                    key: 'custom_title',
                    label: this.$t('common.name'),
                },
                {
                    key: 'visibility',
                    label: this.$t('common.visible'),
                },
                {
                    key: 'connected',
                    label: this.$t('common.connected'),
                },
                {
                    key: 'actions',
                    label: this.$t('common.actions'),
                },
            ],
            ratings: null,
            ratingsForms: [],
            hasError: false,
            modal: false,
            group: this.groupIn,
            ratingoptions: [
                {
                    type: 1,
                    title: this.$t('group.visible_for_everyone'),
                },
                {
                    type: 2,
                    title: this.$t('group.visible_for_teachers'),
                },
            ],
            fields: [
                {
                    key: 'title',
                    label: this.$t('common.title'),
                    sortable: true,
                },
                {
                    key: 'actions',
                    label: this.$t('common.actions'),
                },
            ],
        };
    },

    methods: {
        getRatingforms() {
            let forms = [];

            if (this.ratings.length === 0) {
                return forms;
            }

            forms = this.ratings.map((rating) => {
                return {
                    id: null,
                    rating_id: rating.id,
                    title: rating.title,
                    custom_title: rating.custom_title ? rating.custom_title : null,
                    visibility: null,
                    connected: false,
                    ratingable_id: null,
                };
            });

            if (!this.groupIn && this.groupIn.ratingables.length === 0) {
                return forms;
            }

            for (const ratingable of this.groupIn.ratingables) {
                for (const rating of forms) {
                    if (ratingable.rating_id === rating.rating_id) {
                        rating.id = ratingable.type;
                        rating.connected = true;
                        rating.visibility = ratingable.type;
                        rating.ratingable_id = ratingable.ratingable_id;
                        rating.custom_title = ratingable.title;
                        break;
                    }
                }
            }

            return forms;
        },

        editRatingForm(rating) {
            window.location.href = this.routes.ratingEditForm(rating.rating_id, this.groupIn.id);
        },

        removeRatingForm(id) {
            this.alert.confirm(this.$t('group.delete_form'), this.$t('group.confirm_delete_form')).then((confirm) => {
                if (confirm) {
                    this.alert.saving();
                    this.axios.delete(this.routes.ratingDelete(id)).then(
                        (response) => {
                            this.ratingsForms = this.ratingsForms.filter((rating) => rating.rating_id !== id);
                            this.alert.success();
                        },
                        (error) => {
                            this.alert.ajaxError(error);
                        },
                    );
                }
            });
        },

        onOk(bvModalEvt) {
            if (this.modalHasError()) {
                bvModalEvt.preventDefault();
                return;
            }

            if (this.ratingsForms.length === 0) {
                return;
            }

            let data = null;
            data = new FormData();
            let count = 0;
            let hasRatingables = false;
            for (const ratingable of this.ratingsForms) {
                if (ratingable.connected) {
                    if (ratingable.id !== null) {
                        data.append('ratingsables[' + count + '][id]', ratingable.id);
                        data.append('ratingsables[' + count + '][ratingable_id]', ratingable.ratingable_id);
                    }

                    data.append('ratingsables[' + count + '][title]', ratingable.custom_title);
                    data.append('ratingsables[' + count + '][rating_id]', ratingable.rating_id);
                    data.append('ratingsables[' + count + '][type]', ratingable.visibility);
                    count++;
                    hasRatingables = true;
                }
            }

            if (!hasRatingables) {
                data = {
                    ratings: [],
                };
            }

            const headers = { 'Content-Type': 'multipart/form-data' };
            this.alert.saving();

            this.axios.post(this.routes.ratingGroupAttach(this.group.id), data, { headers: headers }).then(
                (response) => {
                    this.group.ratingables = response.data;
                    this.alert.success();
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );

            this.modal = false;
        },

        show(ratings) {
            this.hasError = false;
            this.ratings = ratings;
            this.ratingsForms = this.getRatingforms();
            this.modal = true;
        },

        modalHasError() {
            this.hasError = false;
            if (this.ratingsForms.length === 0) {
                return this.hasError;
            }

            for (const ratingForm of this.ratingsForms) {
                if (ratingForm.connected && ratingForm.visibility === null) {
                    this.hasError = true;
                    break;
                }
            }

            return this.hasError;
        },

        duplicateForm(id) {
            this.alert
                .confirm(this.$t('group.copy_form'), this.$t('group.confirm_copy_form'), this.$t('group.yes_copy'))
                .then((confirm) => {
                    if (confirm) {
                        location.href = this.routes.ratingDuplicate(id, this.group.id);
                    }
                });
        },
    },
};
</script>

<style scoped lang="scss">
.rating-visibility {
    button.btn {
        font-size: $base-font-size;
        line-height: 1;
        padding: map-get($grid-gutter-spacing, 'eight');
        border: 1px solid transparent;
        background-color: var(--primary);
        color: $white;

        &.disabled {
            filter: grayscale(1);
        }

        &:hover {
            filter: grayscale(0);
            background-color: $white;
            color: var(--primary);
            border-color: var(--primary);
        }

        &.active {
            border-color: var(--primary);
            background-color: $white;
            color: var(--primary);
        }
    }
}

.management-actions {
    button.btn {
        font-size: $base-font-size;
        line-height: 1;
        padding: map-get($grid-gutter-spacing, 'eight');
    }
}
</style>
